// Historique des Paiements - Espace Vendeur
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  CreditCard, Euro, TrendingUp, TrendingDown, Download, Calendar,
  CheckCircle2, Clock, AlertCircle, ChevronRight, ArrowUpRight, ArrowDownLeft,
  Wallet, PiggyBank, BarChart3
} from 'lucide-react';
import { Button } from '../../components/ui/button';

import { API_URL } from '../../config/api';

// Statuts des paiements
const PAYMENT_STATUSES = {
  completed: { color: 'bg-green-100 text-green-700', label: 'Complété', icon: CheckCircle2 },
  pending: { color: 'bg-yellow-100 text-yellow-700', label: 'En attente', icon: Clock },
  failed: { color: 'bg-red-100 text-red-700', label: 'Échoué', icon: AlertCircle },
  refunded: { color: 'bg-gray-100 text-gray-700', label: 'Remboursé', icon: ArrowDownLeft },
};

// Carte statistique
function StatCard({ icon: Icon, label, value, subValue, trend, color }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl p-5 border border-gray-100"
      style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
    >
      <div className="flex items-start justify-between mb-4">
        <div 
          className="w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${color}15` }}
        >
          <Icon size={24} style={{ color }} />
        </div>
        {trend !== undefined && (
          <span className={`flex items-center gap-1 text-sm font-medium ${trend >= 0 ? 'text-green-500' : 'text-red-500'}`}>
            {trend >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
            {Math.abs(trend)}%
          </span>
        )}
      </div>
      <p className="text-3xl font-bold text-gray-900 mb-1">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
      {subValue && <p className="text-xs text-gray-400 mt-1">{subValue}</p>}
    </motion.div>
  );
}

// Ligne de transaction
function TransactionRow({ transaction }) {
  const status = PAYMENT_STATUSES[transaction.status] || PAYMENT_STATUSES.pending;
  const StatusIcon = status.icon;
  const isIncoming = transaction.type === 'incoming';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors rounded-xl"
    >
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
        isIncoming ? 'bg-green-100' : 'bg-red-100'
      }`}>
        {isIncoming ? (
          <ArrowDownLeft size={18} className="text-green-600" />
        ) : (
          <ArrowUpRight size={18} className="text-red-600" />
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <p className="font-medium text-gray-900">{transaction.description}</p>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500">{transaction.date}</span>
          {transaction.orderId && (
            <>
              <span className="text-gray-300">•</span>
              <span className="text-gray-500">#{transaction.orderId}</span>
            </>
          )}
        </div>
      </div>

      <div className="text-right">
        <p className={`font-bold ${isIncoming ? 'text-green-600' : 'text-red-600'}`}>
          {isIncoming ? '+' : '-'}{transaction.amount.toFixed(2)} €
        </p>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
          <StatusIcon size={10} />
          {status.label}
        </span>
      </div>
    </motion.div>
  );
}

export default function PaiementsPage() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [stats, setStats] = useState({
    balance: 4580.50,
    pending: 320.00,
    monthlyRevenue: 2340.00,
    monthlyOrders: 15
  });

  useEffect(() => {
    fetchTransactions();
  }, [period]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      // Données simulées
      setTransactions([
        {
          id: 'PAY-001',
          type: 'incoming',
          description: 'Paiement commande - Perceuse Bosch',
          amount: 149.00,
          date: '15 Jan 2025, 14:35',
          orderId: 'CMD-2024-001',
          status: 'completed'
        },
        {
          id: 'PAY-002',
          type: 'incoming',
          description: 'Paiement commande - Lot chevilles + Vis',
          amount: 64.90,
          date: '14 Jan 2025, 09:20',
          orderId: 'CMD-2024-002',
          status: 'completed'
        },
        {
          id: 'PAY-003',
          type: 'outgoing',
          description: 'Commission plateforme',
          amount: 21.39,
          date: '14 Jan 2025, 09:20',
          status: 'completed'
        },
        {
          id: 'PAY-004',
          type: 'incoming',
          description: 'Paiement commande - Niveau laser',
          amount: 289.00,
          date: '13 Jan 2025, 16:50',
          orderId: 'CMD-2024-003',
          status: 'pending'
        },
        {
          id: 'PAY-005',
          type: 'outgoing',
          description: 'Virement vers compte bancaire',
          amount: 500.00,
          date: '10 Jan 2025, 10:00',
          status: 'completed'
        },
        {
          id: 'PAY-006',
          type: 'outgoing',
          description: 'Remboursement client',
          amount: 89.00,
          date: '8 Jan 2025, 15:30',
          orderId: 'CMD-2023-089',
          status: 'refunded'
        },
        {
          id: 'PAY-007',
          type: 'incoming',
          description: 'Paiement commande - Coffret tournevis',
          amount: 95.90,
          date: '5 Jan 2025, 11:25',
          orderId: 'CMD-2024-004',
          status: 'completed'
        },
      ]);
    } catch (e) {
      console.error('Error fetching transactions:', e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="paiements-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Paiements</h1>
          <p className="text-gray-500">Suis tes revenus et transactions</p>
        </div>
        <div className="flex gap-2">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-xl bg-white text-sm"
          >
            <option value="week">Cette semaine</option>
            <option value="month">Ce mois</option>
            <option value="quarter">Ce trimestre</option>
            <option value="year">Cette année</option>
          </select>
          <Button variant="outline" className="rounded-xl">
            <Download size={16} className="mr-2" />
            Exporter
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={Wallet}
          label="Solde disponible"
          value={`${stats.balance.toLocaleString('fr-FR')} €`}
          color="#22C55E"
        />
        <StatCard
          icon={Clock}
          label="En attente"
          value={`${stats.pending.toFixed(2)} €`}
          subValue="En cours de traitement"
          color="#F59E0B"
        />
        <StatCard
          icon={Euro}
          label="Revenus du mois"
          value={`${stats.monthlyRevenue.toLocaleString('fr-FR')} €`}
          trend={12}
          color="#3B82F6"
        />
        <StatCard
          icon={BarChart3}
          label="Commandes payées"
          value={stats.monthlyOrders}
          trend={8}
          color="#8B5CF6"
        />
      </div>

      {/* Stripe Connect Info */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl p-6 mb-8 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-lg mb-1">Compte Stripe Connect</h3>
            <p className="text-white/80 text-sm">Tes paiements sont sécurisés et gérés par Stripe</p>
          </div>
          <Button className="bg-white text-indigo-600 hover:bg-white/90 rounded-xl">
            <CreditCard size={16} className="mr-2" />
            Gérer mon compte
          </Button>
        </div>
        <div className="mt-4 pt-4 border-t border-white/20 flex items-center gap-6 text-sm">
          <div>
            <p className="text-white/60">IBAN</p>
            <p className="font-mono">FR76 •••• •••• •••• 4532</p>
          </div>
          <div>
            <p className="text-white/60">Prochain virement</p>
            <p>20 Jan 2025</p>
          </div>
          <div>
            <p className="text-white/60">Fréquence</p>
            <p>Hebdomadaire</p>
          </div>
        </div>
      </div>

      {/* Transactions List */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-bold text-gray-900">Historique des transactions</h2>
          <button className="text-sm text-blue-600 hover:underline flex items-center gap-1">
            Voir tout <ChevronRight size={14} />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-4 border-green-500/20 border-t-green-500 rounded-full animate-spin" />
          </div>
        ) : transactions.length === 0 ? (
          <div className="p-12 text-center">
            <CreditCard size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="font-bold text-gray-900 mb-2">Aucune transaction</h3>
            <p className="text-gray-500">Tes transactions apparaîtront ici</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {transactions.map((transaction) => (
              <TransactionRow key={transaction.id} transaction={transaction} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
